
import { select, takeLatest, takeEvery } from 'redux-saga/effects';
import R from 'ramda';
import * as constPF from '../stores/partner-finder/partnerFinder-constants';
import * as constCO from '../stores/connections/connections-constants';
import moment from 'moment';

/*
	Middleware to use for 3rd Party Tracking Scripts
*/

const getUserId = state => state.user.get('id');
const getUserEmail = state => state.user.get('user').get('email');
const getisAdmin = state => state.user.get('isAnAdmin');
const getInvitationsSent = state => state.tempConnections.get('emailStatuses').sent;
const getSignUp = state => state.signup.toJS();
const getAssetsLandingReducer = state => state.assetsLandingReducer;

const sendIntercom = (...params) => {
  if (window.Intercom) {
    window.Intercom(...params); // eslint-disable-line new-cap
  }
};

/*
	Loop through all Google Analytics trackers and send event
*/
const gaSendEvent = ({ eventCategory, eventValue, eventAction, eventLabel }) => {
  window.dataLayer.push({
    event: 'TRACK_EVENT',
    ev: {
      category: eventCategory,
      action: eventAction,
      label: eventLabel,
      value: eventValue,
    },
  });
};

window.dataLayer = window.dataLayer || [];

function* tagAsAdmin() {
  if (yield select(getisAdmin)) {
    window.dataLayer.push({ isTCAdminSession: true });
  }
}

function* rollbarConfigure() {
  if (window.Rollbar) {
    window.Rollbar.configure({
      payload: {
        person: {
          id: yield select(getUserId),
          email: yield select(getUserEmail),
        },
      },
    });
  }
}

function* trackEvent({ payload }) {
  if (payload.name) {
    sendIntercom('trackEvent', payload.name, {
      ...payload.values,
    });
  }
  if (payload.ga) {
    gaSendEvent(payload.ga);
  }
}

function* pageView({ payload }) {
  window.dataLayer.push({
    event: 'PAGE_VIEW',
    document: {
      pagePath: payload.path,
      pageTitle: payload.title,
    },
  });
}

function* sendUserDataToTag({ user }) {
  const {
    id: userId,
    user: {
      name,
      email,
      createdAt,
      intercomApp,
      intercomUserHash,
    },
    isMarketingReady,
    company: {
      companyId,
      companyName,
      companyType,
      plan,
      productType,
      websiteUrl,
      phone,
      acctCreationReason,
      createdAt: companyCreatedAt,
    },
    bookingSystem,
    emailClient,
    affiliateURL,
  } = user;

  const payload = {
    event: 'GET_USER',
    user: {
      id: userId,
      name: name || '',
      email,
      createdAt: new Date(createdAt).getTime() / 1000,
      app_id: intercomApp,
      user_hash: intercomUserHash,
    },
    company: {
      id: companyId,
      createdAt: new Date(companyCreatedAt).getTime() / 1000,
      accountAge: moment().diff(companyCreatedAt, 'days'),
      creationReason: acctCreationReason,
      type: companyType,
      plan,
      name: companyName,
      website: websiteUrl,
      phone,
      isMarketingReady,
      productType,
      bookingSystem,
      emailClient,
      affiliateURL,
    },
  };
  window.dataLayer.push(payload);
}

function sendUserDataToIntercom({ user }) {
  const {
    user: {
      name,
      email,
      intercomApp,
      intercomUserHash,
    },
    company: {
      companyId,
      companyName,
      companyType,
      plan,
      websiteUrl,
      phone,
      acctCreationReason,
    },
    bookingSystem,
    emailClient,
    affiliateURL,
  } = user;
  setTimeout(() => {
    sendIntercom('boot', {
      app_id: intercomApp,
      // user_id: userId // DO NOT SEND USER ID as we are using user_hash base on email
      name: name || '',
      email,
      user_hash: intercomUserHash,
      company: {
        id: companyId,
        creation_reason: acctCreationReason,
        plan,
        company_type: companyType,
        name: companyName,
        website: websiteUrl,
        phone,
        booking_system: bookingSystem,
        email_platform: emailClient,
        affiliateURL,
      },
    });
  }, 3e3);
}

function sendTokenDataToTag({ payload }) {
  const {
    userId,
    email,
    companyName,
    companyId,
    viewAs,
    intercomUserHash,
  } = payload;
  window.dataLayer.push(
    { event: 'GET_USER',
      user: {
        id: userId || email,
        email,
        user_hash: intercomUserHash,
      },
      company: {
        id: companyId,
        name: companyName,
        type: viewAs,
      },
    }
  );
}
function* gaEvent({ type, payload = {} }) {
  const event = {
    STARTING_DRAFT_CREATE_IN_TEMPLATE_LIST: { // Click on 'Created Template' button or clicked on request from Contract List
      eventCategory: 'template',
      eventAction: 'start_template',
    },
    LOADED_TEMPLATES_SUPPLIER_SEND_TEMPLATE_WIZARD: {
      eventCategory: 'template',
      eventAction: 'start_template',
    },
    PUBLISH_TEMPLATE_DRAFT_SUCCESS: { // Clicked to Enter e-Signature
      eventCategory: 'template',
      eventAction: payload.originFrom === 'initial' ? 'complete_initial_template' : 'complete_edited_template',
    },
    CREATE_CONTRACT_FINISHED: {// Buyer sent a contract request
      eventCategory: 'contract',
      eventAction: 'send_request',
    },
    CONTRACTS_CREATED_SUPPLIER_SEND_TEMPLATE_WIZARD: {// Supplier Sent Rates
      eventCategory: 'template',
      eventAction: 'send_rates',
    },
    SEARCH: {
      eventCategory: 'partner-finder',
      eventAction: 'search',
    },
    [constPF.SEND_INVITATION_PF]: {
      eventCategory: 'partner-finder',
      eventAction: 'send-invitation',
    },
    [constPF.ACCEPT_CONNECTION_PF]: {
      eventCategory: 'connection',
      eventAction: 'accept',
    },
    [constPF.IGNORE_CONNECTION_PF]: {
      eventCategory: 'connection',
      eventAction: 'reject',
    },
    [constCO.IGNORE_CONNECTION]: {
      eventCategory: 'connection',
      eventAction: 'reject',
    },
  }[type];
  if (event) gaSendEvent(event);
}

function* intercomEvent({ type }) {
  const message = {
    CREATE_CONTRACT_FINISHED: '[Contract] Send Request',
    STATE_CHANGE_CONTRACT_LIST: '[Contract] Change Status',
    ONBOARDING_COMPLETE: '[Account] Completed Onboarding',
    ADD_PRODUCT: '[Location] Added Product',
    SENT_MARKETING_MATERIALS: '[Marketing Material] Share',
    ADD_LOCATION: 'Added Location',
    ADD_NEW_USERS: '[Settings] Added New User',
    SEARCH: '[Partner Finder] Search',
    [constPF.SEND_INVITATION_PF]: '[Partner Finder] Send Invitation',
    [constPF.RESEND_INVITATION_PF]: '[Partner Finder] Re-Send Invitation',
    [constPF.ACCEPT_CONNECTION_PF]: '[Connection] Accepted Connection',
    [constPF.IGNORE_CONNECTION_PF]: '[Connection] Rejected Connection',
    [constCO.IGNORE_CONNECTION]: '[Connection] Rejected Connection',
    HELPWIDGET_OPEN: '[Support] Widget Open',
    SIGN_UP_USER_FINISHED: '[Sign Up] Create Account',
    '[asset-landing]SIGN_UP_SUCCESS': '[Sign Up] Create Account',
    'AITools_Clicked': 'AITools_Clicked',
  }[type];
  if (message) sendIntercom('trackEvent', message);
}

// Buyer changed the status of a contract
function* gaChangeStatus({ payload: { newState } }) {
  gaSendEvent({
    eventCategory: 'contract',
    eventAction: 'change_status',
    eventLabel: newState,
  });
}

function* invitationsEvent() {
  const count = yield select(getInvitationsSent);
  sendIntercom('trackEvent', '[Connections] Invitation', {
    count,
  });
  gaSendEvent({
    eventCategory: 'connection',
    eventValue: count,
    eventAction: 'sent connection',
  });
}

function* intercomLocationFieldUpdate({ payload: { updateField } }) {
  const event = {
    contacts: '[Location] Contact',
    description: '[Location] Description',
    amenities: '[Location] Amenities',
    rating: '[Location] Star Rating',
    address: '[Location] Address',
    website: '[Location] Website',
    phone: '[Location] Phone',
    marketSegments: '[Location] Market Segments',
    companyTypes: '[Location] Company Types',
  }[updateField];
  if (event) {
    sendIntercom('trackEvent', event);
  }
}

function* intercomMMUpdate({ payload: { uploadingFile } = {} }) {
  if (uploadingFile) {
    sendIntercom('trackEvent', '[Location] Marketing Material');
  }
}

function* upgradeEvent({ payload: { plan } = {} }) {
  sendIntercom('trackEvent', '[Billing] Upgrade Plan', { plan });
  gaSendEvent({
    eventCategory: 'account',
    eventAction: 'upgrade',
  });
  if (window.fbq) window.fbq('track', 'Purchase');
}

function* signUpEvent({ type }) {
  const shellSignup = (type === '[asset-landing]SIGN_UP_SUCCESS');
  const signUpObj = shellSignup ? R.pathOr({}, ['dialog', 'newUserInfo'], (yield select(getAssetsLandingReducer))) : yield select(getSignUp);
  window.dataLayer.push({
    event: 'ACCOUNT_SIGN_UP',
    signUpType: shellSignup ? 'shell' : 'full',
    user: {
      email: signUpObj.email,
    },
    company: {
      creationReason: shellSignup ? 'shell' : signUpObj.acctCreationReason,
      name: signUpObj.companyName,
      companyType: signUpObj.companyType,
    },
  });
}

export default [
  takeLatest('GET_USER', tagAsAdmin),
  takeLatest('GET_USER', rollbarConfigure),
  takeLatest('TRACK_EVENT', trackEvent),
  takeEvery('PAGE_VIEW', pageView),
  takeLatest('GET_USER', sendUserDataToTag),
  takeLatest('GET_USER', sendUserDataToIntercom),
  takeLatest('VERIFY_TOKEN_SUCCESS', sendTokenDataToTag),
  takeLatest('STARTING_DRAFT_CREATE_IN_TEMPLATE_LIST', gaEvent),
  takeLatest('LOADED_TEMPLATES_SUPPLIER_SEND_TEMPLATE_WIZARD', gaEvent),
  takeLatest('PUBLISH_TEMPLATE_DRAFT_SUCCESS', gaEvent),
  takeLatest('CONTRACTS_CREATED_SUPPLIER_SEND_TEMPLATE_WIZARD', gaEvent),
  takeLatest('CREATE_CONTRACT_FINISHED', gaEvent),
  takeLatest('CREATE_CONTRACT_FINISHED', intercomEvent),
  takeLatest('STATE_CHANGE_CONTRACT_LIST', gaChangeStatus),
  takeLatest('STATE_CHANGE_CONTRACT_LIST', intercomEvent),
  takeLatest('SEND_EMAIL_TO_TEMP_CONNECTIONS', invitationsEvent),
  takeLatest('ONBOARDING_COMPLETE', intercomEvent),
  takeEvery('EDIT_USER_LOCATION_PROFILE_FIELD_STARTING', intercomLocationFieldUpdate),
  takeLatest('ADD_PRODUCT', intercomEvent),
  takeLatest('UPDATE_MM_PROP', intercomMMUpdate),
  takeLatest('SENT_MARKETING_MATERIALS', intercomEvent),
  takeLatest('ADD_LOCATION', intercomEvent),
  takeLatest('ADD_NEW_USERS', intercomEvent),
  takeEvery('SEARCH', intercomEvent),
  takeEvery('SEARCH', gaEvent),
  takeLatest(constPF.SEND_INVITATION_PF, intercomEvent),
  takeLatest(constPF.RESEND_INVITATION_PF, intercomEvent),
  takeEvery('USER_STORE_PLAN_UPDATE', upgradeEvent),
  takeEvery('UPGRADE_PLAN', upgradeEvent),
  takeEvery('SELECT_PLAN', upgradeEvent),
  takeEvery(constPF.ACCEPT_CONNECTION_PF, intercomEvent),
  takeEvery(constPF.ACCEPT_CONNECTION_PF, gaEvent),
  takeEvery(constPF.IGNORE_CONNECTION_PF, intercomEvent),
  takeEvery(constPF.IGNORE_CONNECTION_PF, gaEvent),
  takeEvery('[asset-landing]SIGN_UP_SUCCESS', signUpEvent),
  takeEvery('SIGN_UP_USER_FINISHED', signUpEvent),
  takeEvery('[asset-landing]SIGN_UP_SUCCESS', intercomEvent),
  takeEvery('SIGN_UP_USER_FINISHED', intercomEvent),
  takeEvery('HELPWIDGET_OPEN', intercomEvent),
  takeEvery('AITools_Clicked', intercomEvent),
];



// WEBPACK FOOTER //
// ./src/app/middleware/thirdPartyTracking.js